import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/lib/i18n';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';

const DEFAULT_COLOR = '#EF694C';

export default function Branding() {
  const { user } = useAuth();
  const { t } = useI18n();
  const tenantId = user?.tenant_id;

  const [form, setForm] = useState({
    company_name: '',
    support_email: '',
    website_url: '',
    logo_url: '',
    primary_color: DEFAULT_COLOR,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tenantId) return;

    setLoading(true);
    api.get(`/tenants/${tenantId}/settings/branding`)
      .then((res) => {
        const branding = res.data?.branding || {};
        setForm({
          company_name: branding.company_name || '',
          support_email: branding.support_email || '',
          website_url: branding.website_url || '',
          logo_url: branding.logo_url || '',
          primary_color: branding.primary_color || DEFAULT_COLOR,
        });
      })
      .catch(() => toast.error(t.error || 'Fehler'))
      .finally(() => setLoading(false));
  }, [tenantId, t.error]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!tenantId) return;

    setSaving(true);
    try {
      const res = await api.put(`/tenants/${tenantId}/settings/branding`, {
        company_name: form.company_name,
        support_email: form.support_email,
        website_url: form.website_url,
        logo_url: form.logo_url,
        primary_color: form.primary_color,
      });

      const branding = res.data?.branding || {};
      setForm({
        company_name: branding.company_name || '',
        support_email: branding.support_email || '',
        website_url: branding.website_url || '',
        logo_url: branding.logo_url || '',
        primary_color: branding.primary_color || DEFAULT_COLOR,
      });

      toast.success(t.brandingSaved || 'Markendesign gespeichert');
    } catch {
      toast.error(t.error || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-5 h-5 text-[#EF694C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="branding-page">
      <div className="mb-6">
        <h2 className="font-heading font-bold text-2xl text-white" data-testid="branding-title">
          {t.brandingTitle || 'Markendesign'}
        </h2>
        <p className="text-sm text-[#a1a1aa] mt-1">
          Passen Sie Firmenname, Support-Kontakt, Website, Logo und Primärfarbe an.
        </p>
      </div>

      <div className="bg-[#121214] border border-white/10 rounded-xl p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">Firmenname</label>
          <input
            value={form.company_name}
            onChange={(e) => updateField('company_name', e.target.value)}
            data-testid="branding-company-name"
            className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">Support-E-Mail</label>
          <input
            type="email"
            value={form.support_email}
            onChange={(e) => updateField('support_email', e.target.value)}
            data-testid="branding-support-email"
            className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">Website</label>
          <input
            value={form.website_url}
            onChange={(e) => updateField('website_url', e.target.value)}
            placeholder="https://www.onfyre.de"
            data-testid="branding-website"
            className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">Logo-URL</label>
          <input
            value={form.logo_url}
            onChange={(e) => updateField('logo_url', e.target.value)}
            placeholder="https://www.onfyre.de/logo.svg"
            data-testid="branding-logo"
            className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">Primärfarbe</label>
          <div className="flex gap-3">
            <input
              type="color"
              value={form.primary_color}
              onChange={(e) => updateField('primary_color', e.target.value)}
              data-testid="branding-color-picker"
              className="h-10 w-14 bg-[#09090b] border border-white/10 rounded-lg"
            />
            <input
              value={form.primary_color}
              onChange={(e) => updateField('primary_color', e.target.value)}
              data-testid="branding-color-input"
              className="flex-1 h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm font-mono focus:outline-none focus:border-[#EF694C]"
            />
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-branding"
          className="h-10 px-4 rounded-lg bg-[#EF694C] hover:bg-[#d65d43] disabled:opacity-60 text-white text-sm font-semibold inline-flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? (t.saving || 'Speichern...') : (t.save || 'Speichern')}
        </button>
      </div>
    </div>
  );
}
